package com.yendo.mx.ui.auth

import androidx.compose.animation.*
import androidx.compose.animation.core.tween
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.getPlatform
import com.yendo.mx.utils.esAndroid
import com.yendo.mx.utils.rememberGoogleAuthLauncher
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import yendo.shared.generated.resources.*
import coil3.compose.LocalPlatformContext

// 🚀 LOTTIE IMPORTS
import io.github.alexzhirkevich.compottie.Compottie
import io.github.alexzhirkevich.compottie.LottieCompositionSpec
import io.github.alexzhirkevich.compottie.rememberLottieComposition
import io.github.alexzhirkevich.compottie.rememberLottiePainter
import org.jetbrains.compose.resources.ExperimentalResourceApi

enum class AuthStep {
    LANDING,
    ASK_PHONE,
    VERIFY_OTP,
    COMPLETE_PROFILE
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuthScreen(
    onLoginSuccess: () -> Unit,
    onGuestClick: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val context = LocalPlatformContext.current 
    val focusManager = LocalFocusManager.current

    // 🚀 ESTADOS DE NAVEGACIÓN BLINDADOS
    var step by rememberSaveable { mutableStateOf<AuthStep>(AuthStep.LANDING) }
    var telefonoTarget by rememberSaveable { mutableStateOf("") }
    var verificationIdTarget by rememberSaveable { mutableStateOf("") } 
    
    var nombreSocial by rememberSaveable { mutableStateOf("") }
    var apellidoSocial by rememberSaveable { mutableStateOf("") }
    var emailSocial by rememberSaveable { mutableStateOf("") }
    var mensajeVincular by rememberSaveable { mutableStateOf("") } 

    var isLocalLoading by remember { mutableStateOf(false) } 
    val isSocialLoginInProgress by AuthRepository.isSocialLoginInProgress.collectAsState()
    val usuarioState by AuthRepository.usuarioActual.collectAsState()

    // 🚀 ESTADO PARA CONFIRMACIÓN DE IDENTIDAD (BOTTOM SHEET)
    var cuentaExistenteDetectada by rememberSaveable { mutableStateOf<com.yendo.mx.models.UsuarioYendo?>(null) }
    var emailSocialDetectado by rememberSaveable { mutableStateOf("") }
    var fusionPendiente by rememberSaveable { mutableStateOf(false) } // 🚀 NUEVO: Flag persistente de fusión
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)

    fun resetFlujoRegistro() {
        nombreSocial = ""; apellidoSocial = ""; emailSocial = ""
        telefonoTarget = ""; verificationIdTarget = ""; mensajeVincular = ""
        cuentaExistenteDetectada = null; emailSocialDetectado = ""; fusionPendiente = false
    }

    // 🚀 LÓGICA DE NAVEGACIÓN "LEAN"
    LaunchedEffect(usuarioState, isSocialLoginInProgress, isLocalLoading, step) {
        val user = usuarioState
        if (isSocialLoginInProgress || isLocalLoading || mensajeVincular.isNotBlank()) return@LaunchedEffect

        if (user != null && !user.esInvitado) {
            // 🚀 SINCRONIZACIÓN PERMANENTE DE DATOS
            if (telefonoTarget.isBlank() && user.telefono.isNotBlank()) {
                telefonoTarget = user.telefono
            }
            if (nombreSocial.isBlank() && user.nombre.isNotBlank()) {
                nombreSocial = user.nombre
                apellidoSocial = user.apellido
            }

            val tieneNombre = user.nombre.isNotBlank()
            val tieneTelefono = user.telefono.isNotBlank()
            
            if (tieneNombre && tieneTelefono) {
                if (step == AuthStep.LANDING) {
                    println("YENDO_LOG ✅ Usuario completo. Entrando al Home...")
                    onLoginSuccess()
                }
            } else {
                // 🚀 CONTINUIDAD INTELIGENTE (Estilo Uber)
                if (step == AuthStep.LANDING && !isSocialLoginInProgress && !isLocalLoading) {
                    if (tieneTelefono) {
                        println("YENDO_LOG 🛡️ Registro a medias (Tiene Teléfono). Continuando...")
                        step = AuthStep.COMPLETE_PROFILE
                    } else {
                        println("YENDO_LOG 🛡️ Registro a medias (Falta Teléfono). Continuando...")
                        step = AuthStep.ASK_PHONE
                    }
                }
            }
        }
    }

    val isDark = androidx.compose.foundation.isSystemInDarkTheme()
    val colorFondo = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)

    Surface(
        modifier = Modifier
            .fillMaxSize()
            .pointerInput(Unit) {
                detectTapGestures(onTap = { focusManager.clearFocus() })
            },
        color = colorFondo
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            
            // 🚀 FLUJO DE PANTALLAS
            AnimatedContent(
                targetState = step,
                modifier = Modifier.fillMaxSize(),
                label = "AuthFlow",
                transitionSpec = { fadeIn(animationSpec = tween(400)) togetherWith fadeOut(animationSpec = tween(400)) }
            ) { currentStep ->
                when (currentStep) {
                    AuthStep.LANDING -> {
                        // 🚀 LANDING CON LOGO Y BOTONES (Todo en la pantalla principal)
                        val landingScrollState = rememberScrollState()
                        LaunchedEffect(landingScrollState.isScrollInProgress) {
                            if (landingScrollState.isScrollInProgress) {
                                focusManager.clearFocus()
                            }
                        }
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(24.dp)
                                .verticalScroll(landingScrollState)
                                .imePadding(), 
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Spacer(modifier = Modifier.height(100.dp))
                            Image(
                                painter = painterResource(Res.drawable.yendo_logo_vertical_rosa), 
                                contentDescription = "Logo", 
                                modifier = Modifier.width(220.dp).height(90.dp)
                            )
                            Text("Todo lo que quieras, a domicilio", color = Color.Gray, fontSize = 16.sp)
                            Spacer(modifier = Modifier.height(100.dp))
                            
                            PantallaLandingAuth(
                                onGuestClick = onGuestClick,
                                onPhoneClick = { resetFlujoRegistro(); step = AuthStep.ASK_PHONE },
                                onCollisionVincular = { mensajeVincular = it },
                                onSocialAction = { n, a, e -> nombreSocial = n; apellidoSocial = a; emailSocial = e }
                            )
                        }
                    }
                    AuthStep.ASK_PHONE -> {
                        SolicitarTelefonoScreen(
                            onBack = { 
                                scope.launch { 
                                    isLocalLoading = true
                                    AuthRepository.cerrarSesion()
                                    // 🚀 ESPERAR A QUE LA SESIÓN SE CIERRE REALMENTE
                                    var timeout = 0
                                    while (AuthRepository.usuarioActual.value != null && timeout < 20) {
                                        delay(100)
                                        timeout++
                                    }
                                    resetFlujoRegistro()
                                    step = AuthStep.LANDING
                                    isLocalLoading = false
                                } 
                            },
                            onConfirmar = { tel ->
                                isLocalLoading = true
                                scope.launch {
                                    val ownerName = AuthRepository.buscarPropietarioTelefono(tel)
                                    if (ownerName != null) {
                                        // 🚀 ¡Detectado! Mostramos Bottom Sheet antes del SMS
                                        cuentaExistenteDetectada = com.yendo.mx.models.UsuarioYendo(nombre = ownerName, telefono = tel)
                                        fusionPendiente = true // 🚀 Activar modo fusión
                                        isLocalLoading = false
                                    } else {
                                        // Teléfono nuevo, flujo normal
                                        telefonoTarget = tel
                                        fusionPendiente = false
                                        AuthRepository.enviarSmsValidacion(tel, context, { verificationIdTarget = it; isLocalLoading = false; step = AuthStep.VERIFY_OTP }, { isLocalLoading = false })
                                    }
                                }
                            }
                        )
                    }
                    AuthStep.VERIFY_OTP -> {
                        VerificacionCodigoScreen(
                            numeroTelefono = telefonoTarget,
                            verificationIdPrevia = verificationIdTarget,
                            isSocialLoading = isSocialLoginInProgress || isLocalLoading,
                            esFusion = fusionPendiente, // 🚀 FLAG DE SEGURIDAD PERSISTENTE
                            onBack = { 
                                scope.launch { 
                                    isLocalLoading = true
                                    AuthRepository.cerrarSesion()
                                    // 🚀 ESPERAR A QUE LA SESIÓN SE CIERRE REALMENTE
                                    var timeout = 0
                                    while (AuthRepository.usuarioActual.value != null && timeout < 20) {
                                        delay(100)
                                        timeout++
                                    }
                                    resetFlujoRegistro()
                                    step = AuthStep.LANDING
                                    isLocalLoading = false
                                } 
                            },
                            onVerifySuccess = { 
                                scope.launch {
                                    isLocalLoading = true
                                    var user = AuthRepository.usuarioActual.value
                                    var intentos = 0
                                    while ((user == null || user.uid.isEmpty()) && intentos < 15) { delay(300); user = AuthRepository.usuarioActual.value; intentos++ }
                                    if (user != null && user.nombre.isNotBlank()) {
                                        onLoginSuccess() 
                                    } else {
                                        step = AuthStep.COMPLETE_PROFILE
                                    }
                                    isLocalLoading = false
                                }
                            }
                        )
                    }
                    AuthStep.COMPLETE_PROFILE -> {
                        CompletarPerfilScreen(
                            nombreSugerido = nombreSocial,
                            apellidoSugerido = apellidoSocial,
                            emailSugerido = emailSocial,
                            telefonoVerificado = telefonoTarget, 
                            onSuccess = onLoginSuccess,
                            onCancel = { 
                                scope.launch { 
                                    isLocalLoading = true
                                    AuthRepository.cerrarSesion()
                                    // 🚀 ESPERAR A QUE LA SESIÓN SE CIERRE REALMENTE
                                    var timeout = 0
                                    while (AuthRepository.usuarioActual.value != null && timeout < 20) {
                                        delay(100)
                                        timeout++
                                    }
                                    resetFlujoRegistro()
                                    step = AuthStep.LANDING
                                    isLocalLoading = false
                                } 
                            }
                        )
                    }
                }
            }

            // 🚀 OVERLAYS
            if (isSocialLoginInProgress || isLocalLoading) {
                LoadingOverlayYendo()
            }

            // 🚀 CONFIRMACIÓN DE IDENTIDAD EN BOTTOM SHEET
            if (cuentaExistenteDetectada != null) {
                val user = cuentaExistenteDetectada!!
                ModalBottomSheet(
                    onDismissRequest = { cuentaExistenteDetectada = null },
                    sheetState = sheetState,
                    containerColor = if (isDark) Color(0xFF1E1E1E) else Color.White,
                    dragHandle = { BottomSheetDefaults.DragHandle() },
                    shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp)
                ) {
                    Box(modifier = Modifier.fillMaxWidth().fillMaxHeight(0.5f)) {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp).padding(bottom = 40.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Icon(Icons.Default.AccountCircle, null, modifier = Modifier.size(64.dp), tint = RosaYendo)
                            Spacer(Modifier.height(16.dp))
                            Text("¿Eres tú, ${user.nombre}?", fontSize = 22.sp, fontWeight = FontWeight.Bold, color = if (isDark) Color.White else Color.Black)
                            Spacer(Modifier.height(8.dp))
                            Text(
                                text = "Hemos encontrado tu cuenta vinculada al número ${user.telefono}.\n" +
                                       "¿Deseas usar ${emailSocialDetectado.ifBlank { "esta cuenta" }} para entrar?",
                                textAlign = TextAlign.Center,
                                color = Color.Gray,
                                fontSize = 15.sp
                            )
                            Spacer(Modifier.height(32.dp))
                            Button(
                                onClick = { 
                                    val tel = user.telefono
                                    cuentaExistenteDetectada = null
                                    telefonoTarget = tel
                                    isLocalLoading = true
                                    AuthRepository.enviarSmsValidacion(tel, context, { verificationIdTarget = it; isLocalLoading = false; step = AuthStep.VERIFY_OTP }, { isLocalLoading = false })
                                },
                                modifier = Modifier.fillMaxWidth().height(56.dp),
                                colors = ButtonDefaults.buttonColors(RosaYendo),
                                shape = RoundedCornerShape(16.dp)
                            ) { Text("Sí, enviar SMS de seguridad", color = Color.White, fontWeight = FontWeight.Bold) }
                            Spacer(Modifier.height(12.dp))
                            TextButton(onClick = { scope.launch { AuthRepository.cerrarSesion() }; resetFlujoRegistro(); step = AuthStep.LANDING }, modifier = Modifier.fillMaxWidth()) {
                                Text("No soy yo, usar otra cuenta", color = RosaYendo, fontWeight = FontWeight.Bold)
                            }
                        }
                    }
                }
            }
        }

        // 🚀 PROCESADOR DE COLISIONES
        LaunchedEffect(mensajeVincular) {
            if (mensajeVincular.startsWith("COLLISION_VINCULAR|")) {
                val partes = mensajeVincular.split("|")
                val tel = partes.getOrNull(1) ?: ""
                val nombre = partes.getOrNull(2) ?: ""
                val emailS = partes.getOrNull(3) ?: ""
                
                fusionPendiente = true // 🚀 Activar modo fusión desde colisión social
                
                // 🚀 PRESERVAR DATOS SOCIALES: Para que lleguen a Completar Perfil si es necesario
                nombreSocial = nombre
                emailSocial = emailS
                
                if (nombre.isNotBlank()) {
                    cuentaExistenteDetectada = com.yendo.mx.models.UsuarioYendo(nombre = nombre, telefono = tel)
                    emailSocialDetectado = emailS
                    mensajeVincular = ""
                } else {
                    telefonoTarget = tel
                    mensajeVincular = ""
                    isLocalLoading = true
                    AuthRepository.enviarSmsValidacion(tel, context, { verificationIdTarget = it; isLocalLoading = false; step = AuthStep.VERIFY_OTP }, { isLocalLoading = false })
                }
            }
        }
    }
}

@OptIn(ExperimentalResourceApi::class)
@Composable
fun LoadingOverlayYendo() {
    val composition by rememberLottieComposition { LottieCompositionSpec.JsonString(Res.readBytes("files/yendo_cargando.json").decodeToString()) }
    Box(modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.4f)).clickable(enabled = false) {}, contentAlignment = Alignment.Center) {
        Card(shape = RoundedCornerShape(24.dp), colors = CardDefaults.cardColors(containerColor = if (androidx.compose.foundation.isSystemInDarkTheme()) Color(0xFF1E1E1E) else Color.White)) {
            Box(modifier = Modifier.padding(24.dp), contentAlignment = Alignment.Center) {
                Image(painter = rememberLottiePainter(composition = composition, iterations = Compottie.IterateForever), contentDescription = "Cargando", modifier = Modifier.size(80.dp))
            }
        }
    }
}

@Composable
fun PantallaLandingAuth(
    onGuestClick: () -> Unit,
    onPhoneClick: () -> Unit,
    onCollisionVincular: (String) -> Unit,
    onSocialAction: (String, String, String) -> Unit
) {
    var mensajeErrorGlobal by remember { mutableStateOf("") }
    val isDark = androidx.compose.foundation.isSystemInDarkTheme()
    val colorBorde = if (isDark) Color(0xFF3A3A3C) else Color(0xFFE0E0E0)
    val colorTarjeta = if (isDark) Color(0xFF1E1E1E) else Color.White
    val scope = rememberCoroutineScope()

    // 🚀 OBTENEMOS CONFIGURACIÓN GLOBAL
    val config = com.yendo.mx.repository.ConfigGlobalRepository.configCargada

    val lanzarGoogleAuth = rememberGoogleAuthLauncher { idToken, nombreCompleto, email, foto ->
        if (idToken != null) {
            scope.launch {
                val partes = (nombreCompleto ?: "Usuario").split(" ")
                onSocialAction(partes.first(), if (partes.size > 1) partes.drop(1).joinToString(" ") else "", email ?: "")
                
                println("YENDO_LOG 🚀 [AuthScreen] Llamando loginConGoogle con foto: $foto")
                
                val res = AuthRepository.loginConGoogle(idToken, partes.first(), "", email ?: "", foto)
                if (res.startsWith("COLLISION_VINCULAR|")) {
                    onCollisionVincular(res)
                } else if (res == "REQUIERE_TELEFONO") {
                    // 🚀 SALTO AUTOMÁTICO AL TELÉFONO
                    onPhoneClick()
                } else if (res != "CANCELADO" && res != "OK") {
                    mensajeErrorGlobal = traducirErrorAuth(res)
                }
            }
        }
    }

    Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
        if (config?.auth_telefono_activo != false) {
            Button(onClick = onPhoneClick, modifier = Modifier.fillMaxWidth().height(56.dp), colors = ButtonDefaults.buttonColors(RosaYendo), shape = RoundedCornerShape(16.dp)) {
                Icon(Icons.Default.PhoneAndroid, null, tint = Color.White); Spacer(Modifier.width(12.dp))
                Text("Continuar con mi Teléfono", fontWeight = FontWeight.Bold, color = Color.White)
            }
            Spacer(Modifier.height(16.dp))
        }

        if (config?.auth_google_activo != false) {
            BotonSocial("Continuar con Google", colorTarjeta, if (isDark) Color.White else Color.Black, painterResource(Res.drawable.google_g_logo), BorderStroke(1.dp, colorBorde)) {
                if (esAndroid() || getPlatform().name.contains("Web")) lanzarGoogleAuth() else scope.launch {
                    val res = AuthRepository.iniciarSesionConGoogle()
                    if (res == "OK") {
                        val n = AuthRepository.obtenerNombreSocial() ?: ""
                        val a = AuthRepository.obtenerApellidoSocial() ?: ""
                        val e = AuthRepository.obtenerEmailSocial() ?: ""
                        onSocialAction(n, a, e)
                    } else if (res.startsWith("COLLISION_VINCULAR|")) {
                        onCollisionVincular(res)
                    } else if (res != "CANCELADO") {
                        mensajeErrorGlobal = traducirErrorAuth(res)
                    }
                }
            }
        }

        if (config?.auth_apple_activo != false && !esAndroid()) {
            Spacer(Modifier.height(12.dp))
            BotonSocial("Continuar con Apple", colorTarjeta, if (isDark) Color.White else Color.Black, painterResource(Res.drawable.apple_logo_black), BorderStroke(1.dp, colorBorde), tint = if (isDark) Color.White else Color.Black) {
                scope.launch {
                    val res = AuthRepository.iniciarSesionConApple()
                    if (res == "OK") {
                        val n = AuthRepository.obtenerNombreSocial() ?: ""
                        val a = AuthRepository.obtenerApellidoSocial() ?: ""
                        val e = AuthRepository.obtenerEmailSocial() ?: ""
                        onSocialAction(n, a, e)
                    } else if (res.startsWith("COLLISION_VINCULAR|")) {
                        onCollisionVincular(res)
                    } else if (res != "CANCELADO") {
                        mensajeErrorGlobal = traducirErrorAuth(res)
                    }
                }
            }
        }
        
        if (mensajeErrorGlobal.isNotEmpty()) Text(mensajeErrorGlobal, color = Color.Red, fontSize = 12.sp, modifier = Modifier.padding(top = 12.dp))
        Spacer(Modifier.height(32.dp))
        TextButton(onClick = onGuestClick) { Text("Explorar como invitado", color = RosaYendo, fontWeight = FontWeight.Bold) }
    }
}

@Composable
fun SolicitarTelefonoScreen(onBack: () -> Unit, onConfirmar: (String) -> Unit) {
    var telefono by remember { mutableStateOf("") }
    var codigoPais by remember { mutableStateOf("+52") }
    var menuExpanded by remember { mutableStateOf(false) }
    val isDark = androidx.compose.foundation.isSystemInDarkTheme()
    val focusManager = LocalFocusManager.current
    val scrollState = rememberScrollState()

    LaunchedEffect(scrollState.isScrollInProgress) {
        if (scrollState.isScrollInProgress) {
            focusManager.clearFocus()
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .pointerInput(Unit) {
                detectTapGestures(onTap = { focusManager.clearFocus() })
            },
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp)
                .verticalScroll(scrollState)
                .imePadding(), 
            horizontalAlignment = Alignment.CenterHorizontally, 
            verticalArrangement = Arrangement.Center
        ) {
            Icon(Icons.Default.PhoneAndroid, null, modifier = Modifier.size(64.dp), tint = RosaYendo)
            Spacer(Modifier.height(24.dp))
            Text("Tu número de teléfono", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = if(isDark) Color.White else Color.Black)
            Text("Lo usaremos para identificarte.", textAlign = TextAlign.Center, color = Color.Gray, modifier = Modifier.padding(vertical = 16.dp))
            OutlinedTextField(
                value = telefono, onValueChange = { if (it.length <= 10) telefono = it.filter { c -> c.isDigit() } },
                label = { Text("Teléfono celular") }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp),
                leadingIcon = {
                    Box {
                        Row(modifier = Modifier.clickable { menuExpanded = true }.padding(horizontal = 12.dp), verticalAlignment = Alignment.CenterVertically) {
                            Text(codigoPais, fontWeight = FontWeight.Bold, color = RosaYendo); Icon(Icons.Default.ArrowDropDown, null, tint = RosaYendo)
                        }
                        DropdownMenu(expanded = menuExpanded, onDismissRequest = { menuExpanded = false }) {
                            DropdownMenuItem(text = { Text("+52 (MX)") }, onClick = { codigoPais = "+52"; menuExpanded = false })
                            DropdownMenuItem(text = { Text("+1 (US/CA)") }, onClick = { codigoPais = "+1"; menuExpanded = false })
                        }
                    }
                }
            )
            Spacer(Modifier.height(32.dp))
            Button(onClick = { onConfirmar("$codigoPais$telefono") }, modifier = Modifier.fillMaxWidth().height(56.dp), shape = RoundedCornerShape(16.dp), colors = ButtonDefaults.buttonColors(RosaYendo), enabled = telefono.length == 10) {
                Text("Continuar", fontWeight = FontWeight.Bold, color = Color.White)
            }
            Spacer(Modifier.height(16.dp)); TextButton(onClick = onBack) { Text("Atrás", color = Color.Gray) }
        }
    }
}

@Composable
fun CompletarPerfilScreen(nombreSugerido: String, apellidoSugerido: String, emailSugerido: String, telefonoVerificado: String, onSuccess: () -> Unit, onCancel: () -> Unit) {
    val user by AuthRepository.usuarioActual.collectAsState()
    val esSocial = (user?.metodosAcceso?.any { it == "Google" || it == "Apple" } == true) || emailSugerido.isNotBlank()
    // 🚀 MEJORA: Detectar si el email ya viene de Firestore (Usuario recurrente a medias)
    val tieneEmailPrevio = user?.email?.isNotBlank() == true

    var nombre by remember { mutableStateOf(nombreSugerido.ifBlank { user?.nombre ?: "" }) }
    var apellido by remember { mutableStateOf(apellidoSugerido.ifBlank { user?.apellido ?: "" }) }
    var correo by remember { mutableStateOf(user?.email?.ifBlank { emailSugerido } ?: emailSugerido) }
    var confirmaCorreo by remember { mutableStateOf("") }
    var aceptaTerminos by remember { mutableStateOf(false) }

    // 🚀 SINCRONIZACIÓN VISUAL: Si el usuario carga después de la pantalla, actualizamos el correo
    LaunchedEffect(user?.email) {
        if (user?.email?.isNotBlank() == true) {
            correo = user?.email ?: ""
        }
    }

    var isLoading by remember { mutableStateOf(false) }
    var errorMsg by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()
    val uriHandler = LocalUriHandler.current
    val focusManager = LocalFocusManager.current
    val scrollState = rememberScrollState()

    LaunchedEffect(scrollState.isScrollInProgress) {
        if (scrollState.isScrollInProgress) {
            focusManager.clearFocus()
        }
    }

    // 🚀 REGLA DE CANDADO REFORZADA: Directo de Auth + Firestore
    val authProviders = Firebase.auth.currentUser?.providerData?.map { it.providerId } ?: emptyList<String>()
    val esSocialDirecto = authProviders.any { it.contains("google") || it.contains("apple") }
    val emailBloqueado = esSocialDirecto || esSocial || tieneEmailPrevio

    Box(
        modifier = Modifier
            .fillMaxSize()
            .pointerInput(Unit) {
                detectTapGestures(onTap = { focusManager.clearFocus() })
            },
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp)
                .verticalScroll(scrollState)
                .imePadding(), 
            horizontalAlignment = Alignment.CenterHorizontally, 
            verticalArrangement = Arrangement.Center
        ) {
            Icon(Icons.Default.Badge, null, modifier = Modifier.size(64.dp), tint = RosaYendo)
            Spacer(Modifier.height(16.dp))
            Text("Crea tu perfil", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = if(androidx.compose.foundation.isSystemInDarkTheme()) Color.White else Color.Black)
            Spacer(Modifier.height(24.dp))
            OutlinedTextField(value = nombre, onValueChange = { nombre = it }, label = { Text("Nombre(s)") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(Modifier.height(12.dp))
            OutlinedTextField(value = apellido, onValueChange = { apellido = it }, label = { Text("Apellidos") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(Modifier.height(12.dp))
            
            // 🚀 BLOQUEO DE EMAIL: Si es social o ya existe, no se edita
            OutlinedTextField(
                value = correo, 
                onValueChange = { if (!emailBloqueado) correo = it }, 
                label = { Text(if (emailBloqueado) "Correo verificado" else "Correo electrónico") }, 
                enabled = !emailBloqueado, 
                modifier = Modifier.fillMaxWidth(), 
                shape = RoundedCornerShape(12.dp), 
                leadingIcon = { Icon(if (emailBloqueado) Icons.Default.Lock else Icons.Default.Email, null, tint = if (emailBloqueado) Color.Gray else RosaYendo) },
                colors = OutlinedTextFieldDefaults.colors(
                    disabledTextColor = if (androidx.compose.foundation.isSystemInDarkTheme()) Color.White.copy(alpha = 0.6f) else Color.Black.copy(alpha = 0.6f),
                    disabledBorderColor = Color.Gray.copy(alpha = 0.5f),
                    disabledLabelColor = Color.Gray
                )
            )
            
            if (!emailBloqueado) {
                Spacer(Modifier.height(12.dp))
                OutlinedTextField(value = confirmaCorreo, onValueChange = { confirmaCorreo = it }, label = { Text("Confirmar correo") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            }
            Spacer(Modifier.height(12.dp))
            OutlinedTextField(
                value = telefonoVerificado.ifBlank { user?.telefono ?: "" }, 
                onValueChange = {}, 
                label = { Text("Teléfono verificado") }, 
                enabled = false, 
                modifier = Modifier.fillMaxWidth(), 
                shape = RoundedCornerShape(12.dp), 
                trailingIcon = { Icon(Icons.Default.CheckCircle, null, tint = Color(0xFF4CAF50)) }
            )
            Spacer(Modifier.height(16.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(checked = aceptaTerminos, onCheckedChange = { aceptaTerminos = it }, colors = CheckboxDefaults.colors(RosaYendo))
                Text("Acepto los Términos y Condiciones", fontSize = 12.sp, color = Color.Gray, modifier = Modifier.clickable { uriHandler.openUri("https://www.yendo.com.mx/terminos-y-condiciones") })
            }
            if (errorMsg.isNotBlank()) Text(traducirErrorAuth(errorMsg), color = Color.Red, fontSize = 12.sp, modifier = Modifier.padding(top = 16.dp))
            Spacer(Modifier.height(32.dp))

            // 🚀 VALIDACIÓN INTELIGENTE: Si el email ya está bloqueado/verificado, no pedimos confirmación
            val isFormValid = nombre.length >= 2 && apellido.length >= 2 && 
                             (emailBloqueado || (correo == confirmaCorreo && correo.contains("@"))) && 
                             aceptaTerminos

            Button(
                onClick = {
                    isLoading = true; scope.launch {
                        val res = AuthRepository.actualizarPerfilBase(nombre, apellido, correo, telefonoVerificado)
                        if (res == "OK") onSuccess() else { errorMsg = res; isLoading = false }
                    }
                },
                modifier = Modifier.fillMaxWidth().height(56.dp), shape = RoundedCornerShape(16.dp), colors = ButtonDefaults.buttonColors(RosaYendo),
                enabled = !isLoading && isFormValid
            ) { if (isLoading) CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp)) else Text("Finalizar y Entrar", fontWeight = FontWeight.Bold, color = Color.White) }
            Spacer(Modifier.height(16.dp)); TextButton(onClick = onCancel) { Text("Cancelar y salir", color = Color.Gray) }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun VerificacionCodigoScreen(
    numeroTelefono: String, 
    verificationIdPrevia: String, 
    isSocialLoading: Boolean, 
    esFusion: Boolean = false, // 🚀 NUEVO
    onBack: () -> Unit, 
    onVerifySuccess: () -> Unit
) {
    val context = LocalPlatformContext.current
    val focusManager = LocalFocusManager.current
    val scrollState = rememberScrollState()
    var codigo by remember { mutableStateOf("") }
    var verId by remember { mutableStateOf(verificationIdPrevia) } 
    var mensajeError by remember { mutableStateOf("") }
    
    LaunchedEffect(Unit) { if (verId.isEmpty()) AuthRepository.enviarSmsValidacion(numeroTelefono, context, { verId = it }, { mensajeError = it }) }

    LaunchedEffect(scrollState.isScrollInProgress) {
        if (scrollState.isScrollInProgress) {
            focusManager.clearFocus()
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .pointerInput(Unit) {
                detectTapGestures(onTap = { focusManager.clearFocus() })
            },
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp)
                .verticalScroll(scrollState)
                .imePadding(), 
            horizontalAlignment = Alignment.CenterHorizontally, 
            verticalArrangement = Arrangement.Center
        ) {
            Icon(Icons.Default.Email, null, modifier = Modifier.size(64.dp), tint = RosaYendo); Spacer(Modifier.height(24.dp))
            Text("Verifica el código", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = if(androidx.compose.foundation.isSystemInDarkTheme()) Color.White else Color.Black)
            Text("Ingresa los 6 dígitos enviados al $numeroTelefono", textAlign = TextAlign.Center, color = Color.Gray, modifier = Modifier.padding(vertical = 16.dp))
            
            // 🚀 CAMPO OTP ESTILIZADO (Igual que en Comercio/Repartidor)
            OutlinedTextField(
                value = codigo, 
                onValueChange = { 
                    val cleaned = it.filter { c -> c.isDigit() }
                    if (cleaned.length <= 6) codigo = cleaned 
                }, 
                placeholder = { Text("000000", modifier = Modifier.fillMaxWidth(), textAlign = TextAlign.Center) },
                modifier = Modifier.width(200.dp),
                textStyle = LocalTextStyle.current.copy(
                    textAlign = TextAlign.Center,
                    fontSize = 24.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 8.sp
                ),
                shape = RoundedCornerShape(12.dp), 
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                singleLine = true
            )
            
            if (mensajeError.isNotEmpty()) Text(traducirErrorAuth(mensajeError), color = Color.Red, fontSize = 12.sp, modifier = Modifier.padding(top = 8.dp), textAlign = TextAlign.Center)
            Spacer(Modifier.height(32.dp))
            Button(
                onClick = { 
                    AuthRepository.confirmarSmsYFinalizar(
                        verificationId = verId, 
                        codigo = codigo, 
                        esFusion = esFusion, // 🚀 PASAMOS EL FLAG
                        onSuccess = { onVerifySuccess() }, 
                        onError = { mensajeError = it }
                    ) 
                },
                modifier = Modifier.fillMaxWidth().height(56.dp), shape = RoundedCornerShape(16.dp), colors = ButtonDefaults.buttonColors(containerColor = RosaYendo),
                enabled = codigo.length == 6 && !isSocialLoading && verId.isNotEmpty()
            ) { if (isSocialLoading) CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp)) else Text("Verificar", fontWeight = FontWeight.Bold, color = Color.White) }
            Spacer(Modifier.height(16.dp)); TextButton(onClick = onBack) { Text("Atrás", color = Color.Gray) }
        }
    }
}

@Composable
fun BotonSocial(texto: String, colorFondo: Color, colorTexto: Color, icon: androidx.compose.ui.graphics.painter.Painter, border: BorderStroke? = null, tint: Color? = null, onClick: () -> Unit) {
    Button(onClick = onClick, modifier = Modifier.fillMaxWidth().height(52.dp), colors = ButtonDefaults.buttonColors(containerColor = colorFondo), shape = RoundedCornerShape(12.dp), border = border, elevation = ButtonDefaults.buttonElevation(defaultElevation = 2.dp)) {
        if (tint != null) Image(painter = icon, contentDescription = null, modifier = Modifier.size(20.dp), colorFilter = androidx.compose.ui.graphics.ColorFilter.tint(tint))
        else Image(painter = icon, contentDescription = null, modifier = Modifier.size(20.dp))
        Spacer(Modifier.width(12.dp)); Text(texto, color = colorTexto, fontWeight = FontWeight.Bold, fontSize = 14.sp)
    }
}

fun traducirErrorAuth(error: String): String {
    val e = error.lowercase()
    return when {
        e.contains("email_already_in_use") -> {
            val partes = error.split("|")
            val phone = partes.getOrNull(1) ?: ""
            val terminacion = if (phone.length >= 4) phone.takeLast(4) else "****"
            "Este correo ya está registrado en YENDO. Intenta iniciar sesión con tu número de teléfono terminación xxx xxx $terminacion"
        }
        e.contains("account-exists") || e.contains("collision") -> "Este correo ya está registrado. Intenta con tu número de teléfono."
        e.contains("invalid-verification-code") || e.contains("invalid-credential") -> "El código SMS es incorrecto o ya expiró. Verifica e intenta de nuevo."
        e.contains("too-many-requests") || e.contains("activity-limit-exceeded") -> "Demasiados intentos. Tu número ha sido bloqueado temporalmente por seguridad. Intenta más tarde."
        e.contains("network-request-failed") -> "Sin conexión a internet. Revisa tu señal."
        e.contains("timeout") || e.contains("expired") -> "El tiempo de espera se agotó. Revisa que tu número sea correcto e intenta de nuevo."
        else -> "Ocurrió un error: $error. Por favor revisa tus datos e intenta de nuevo."
    }
}
